/*
  EXAMPLE USAGE
  *
  *
  *
  Be sure to inlude the classes "swiper-wrapper" around all the slides and "swiper-slide" on each slide.
  Swiper uses these internally.
  *
  <swiper-carousel
    data-space-between="0"
    data-slides-per-view="1"
    data-centered="true"
    data-centered-bounds="true"
    class=""
  >
    <div class="swiper-wrapper">
      # for item in iterable
      <div class="swiper-slide">
          ..slide content
      </div>
    </div>
  </swiper-carousel>
*/

if (!customElements.get('swiper-carousel')) {
  class Carousel extends HTMLElement {
    constructor() {
      super();

      this.showPagination = this.dataset.pagination === 'true' ? {
        clickable: true,
        el: '[data-pagination]',
        type: 'bullets',
        bulletActiveClass: `${this.dataset.bulletActiveClass} swiper-pagination-bullet-active` || 'swiper-pagination-bullet-active',
        bulletClass: `${this.dataset.bulletClass} swiper-pagination-bullet` || 'swiper-pagination-bullet',
        dynamicBullets: this.dataset.dynamicBullets === 'true' ?? false,
      } : false;

      this.width = Number(this.dataset.width) || null;
      this.scrollbar = this.dataset.scrollbar === 'true' ? {
        el: '[data-scrollbar]',
        draggable: false,
      } : false;
    }

    connectedCallback() {
      if (window.swiperIsLoaded) {
        this.init();
        this.addResizeListener();
        return;
      } 
      
      window.addEventListener('swiper:script-loaded', () => {
        this.init();
        this.addResizeListener();
      });
    }
  
    addResizeListener() {
      window.addEventListener('resize', debounce(() => {
        if (!this.classList.contains('swiper-initialized')) return;
        this.reinit();
      }), 1000);
    }

    reinit() {
      if (this.closest('.overlay')) return;
      this.slider.destroy();
      this.init();
    }
    
    init() {
      const options = {
        slidesOffsetAfter: Number(this.dataset.mobileSlidesOffsetAfter) || 0,
        slidesOffsetBefore: Number(this.dataset.mobileSlidesOffsetAfter) || 0,
        touchMoveStopPropagation: true,
        touchStartForcePreventDefault:  this.dataset.preventDefault === 'false' ? false : true,
        grabCursor: true,
        freeMode:  this.dataset.freemode === 'true' ? true : false,
        autoplay: this.dataset.autoplay == 'true' ? { delay: this.dataset.delay || 3000} : false,
        initialSlide: this.dataset.initialSlide || 0,
        pagination: this.showPagination,
        scrollbar: this.scrollbar,
        spaceBetween: Number(this.dataset.mobileSpaceBetween) || Number(this.dataset.spaceBetween) || 2,
        preventClicksPropagation: true,
        loop: this.dataset.loop === 'true' ? true : false,
        rewind: this.dataset.rewind === 'true' ? true : false,
        width: this.width || this.parentElement.offsetWidth,
        slidesPerView: Number(this.dataset.slidesPerViewMobile) || 1,
        slidesPerGroup: Number(this.dataset.slidesPerScrollMobile) || 1,
        initialSlide: Number(this.dataset.initialSlide) || 0,
        centeredSlides: !!this.dataset.centered,
        centeredSlidesBounds: !!this.dataset.centered,
        centerInsufficientSlides: !!this.dataset.centered,
        allowTouchMove: this.dataset.allowTouchMove === 'false' ? false : true,
        draggable: true,
        effect: this.dataset.effect || 'slide',
        threshold: this.dataset.threshold || 5,
        watchSlidesProgress: this.dataset.watchSlidesProgress === 'true' ? true : false,
        threshold: this.dataset.threshold || 5,
        navigation: {
          nextEl: this.dataset.externalNavButtons ? document.querySelector(`[data-next="${this.dataset.externalNavButtons}"]`) : this.querySelector('[data-next]'),
          prevEl: this.dataset.externalNavButtons ? document.querySelector(`[data-prev="${this.dataset.externalNavButtons}"]`) :this.querySelector('[data-prev]'),
        },
        breakpoints: {
        768: {
          slidesOffsetAfter: Number(this.dataset.slidesOffsetAfter) || 0,
          slidesOffsetBefore: Number(this.dataset.slidesOffsetAfter) || 0,
          slidesPerGroup: Number(this.dataset.slidesPerScroll) || 1,
          slidesPerView: Number(this.dataset.slidesPerViewTablet) || Number(this.dataset.slidesPerView) || 1,
          spaceBetween: Number(this.dataset.spaceBetweenTablet) || Number(this.dataset.spaceBetween) || 2,
        },
        1280: {
          spaceBetween: Number(this.dataset.spaceBetween) || 2,
          slidesPerView: Number(this.dataset.slidesPerView) || 1,
        }
      },
        thumbs: {
          swiper: this.dataset.thumbs ? document.getElementById(this.dataset.thumbs)?.swiper : null,
        }
      };
    
      this.slider = new Swiper(this, options);
      this.slider.touchEventsData.formElements = 'undefined';

      const tag_elements = document.querySelectorAll(`[data-tag-elem]`);
      const slider_class_list = [...this.slider.wrapperEl.classList];
      const is_mobile_pdp_swiper = slider_class_list.includes('mobile-pdp-swiper'); 
      const is_mobile_breakpoint = screen.width < 769;

      // On init, check if there's badge tags on mobile pdp swiper and show the tag if we are on the first image
      if (is_mobile_pdp_swiper && is_mobile_breakpoint && tag_elements.length > 0 && this.slider.activeIndex === 0) {
        tag_elements.forEach((tag_element) => {
            tag_element.classList.add('visible');
            tag_element.classList.remove('invisible');
        });
      }

      // Hide badges on slide change and active index is not 0
      this.slider.on('slideChange', (slider) => {
        const tag_elements = document.querySelectorAll(`[data-tag-elem]`);
        const active_index = slider.activeIndex;
        const slider_class_list = [...slider.wrapperEl.classList];
        const is_mobile_pdp_swiper = slider_class_list.includes('mobile-pdp-swiper'); 

        if (!is_mobile_pdp_swiper || tag_elements.length === 0) return; 

        tag_elements.forEach((tag_element) => {
          if (is_mobile_breakpoint && active_index !== 0) {
            tag_element.classList.remove('visible');
            tag_element.classList.add('invisible');
          } else if (screen.width < 768 && active_index === 0) {
            tag_element.classList.add('visible');
            tag_element.classList.remove('invisible');
          }
        });

      });
      
      window.addEventListener('coco-shuffle:product-updated', () => {
        if (this.querySelector('[data-product-images]')) {
          this.slider.update();
        }
      });
    }
  }
  customElements.define('swiper-carousel', Carousel);
}